import React, { useState } from "react";

import { PageMain } from "components/molecules";
import { PaziaButton, PaziaLoader, PaziaBadge } from "components/atoms";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { PaziaBox } from "components/atoms";
import { ThemeColors } from "consts/theme";
import {
  useShopRedirects,
  useShopRedirectChains,
  ShopRedirectApi,
  ShopRedirectChain,
  shopRedirectsApi,
} from "api/shop";
import { useToaster } from "utils";

import { ShopRedirectModal, ShopRedirectFormValues } from "./ShopRedirectModal";

export const ShopRedirectsPage = ({ embedded }: { embedded?: boolean }) => {
  const { isLoading, redirects, invalidate } = useShopRedirects();
  const { chains, isLoading: chainsLoading, invalidate: invalidateChains } = useShopRedirectChains();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShopRedirectApi | null>(null);
  const [isFlattening, setIsFlattening] = useState(false);
  const [flatteningChainId, setFlatteningChainId] = useState<number | null>(null);
  const { showSuccess, showError } = useToaster();

  const flattenSummary = (flattened: number, skipped: number) => {
    if (skipped > 0) {
      return `Spłaszczono ${flattened} przekierowań, pominięto ${skipped} cyklicznych`;
    }
    return `Spłaszczono ${flattened} przekierowań`;
  };

  const handleFlattenAll = async () => {
    setIsFlattening(true);
    try {
      const { data } = await shopRedirectsApi.flatten();
      showSuccess(flattenSummary(data.flattenedRedirectsCount, data.skippedCircularCount));
      invalidateChains();
    } catch {
      showError("Nie udało się spłaszczyć łańcuchów");
    } finally {
      setIsFlattening(false);
    }
  };

  const handleFlattenOne = async (chain: ShopRedirectChain) => {
    const firstStepId = chain.steps[0]?.redirectId;
    if (firstStepId == null) return;
    setFlatteningChainId(firstStepId);
    try {
      const { data } = await shopRedirectsApi.flatten([firstStepId]);
      showSuccess(flattenSummary(data.flattenedRedirectsCount, data.skippedCircularCount));
      invalidateChains();
    } catch {
      showError("Nie udało się spłaszczyć łańcucha");
    } finally {
      setFlatteningChainId(null);
    }
  };

  const handleAdd = async (data: ShopRedirectFormValues) => {
    try {
      await shopRedirectsApi.create({
        sourcePath: data.sourcePath,
        destinationPath: data.destinationPath,
        isPermanent: data.isPermanent ?? true,
        isPattern: data.isPattern ?? false,
        description: data.description || undefined,
      });
      showSuccess("Przekierowanie zostało dodane");
      setIsAddOpen(false);
      invalidate();
    } catch {
      showError("Nie udało się dodać przekierowania");
    }
  };

  const handleEdit = async (data: ShopRedirectFormValues) => {
    if (!editTarget) return;
    try {
      await shopRedirectsApi.update(editTarget.id, {
        sourcePath: data.sourcePath,
        destinationPath: data.destinationPath,
        isPermanent: data.isPermanent ?? true,
        isPattern: data.isPattern ?? false,
        description: data.description || undefined,
      });
      showSuccess("Przekierowanie zostało zaktualizowane");
      setEditTarget(null);
      invalidate();
    } catch {
      showError("Nie udało się zaktualizować przekierowania");
    }
  };

  const handleDelete = async (redirect: ShopRedirectApi) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć przekierowanie "${redirect.sourcePath}"?`)) return;
    try {
      await shopRedirectsApi.delete(redirect.id);
      showSuccess("Przekierowanie zostało usunięte");
      invalidate();
    } catch {
      showError("Nie udało się usunąć przekierowania");
    }
  };

  const columns = React.useMemo<TanTableColumn<ShopRedirectApi>[]>(
    () => [
      {
        id: "sourcePath",
        accessorKey: "sourcePath",
        header: "Ścieżka źródłowa",
        enableColumnFilter: false,
        size: 250,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <code className="rounded bg-slate-100 px-2 py-1 text-sm dark:bg-slate-700">
              {info.getValue<string>()}
            </code>
            {info.row.original.isPattern && (
              <PaziaBadge color={"violet" as ThemeColors}>wzorzec</PaziaBadge>
            )}
          </div>
        ),
      },
      {
        id: "destinationPath",
        accessorKey: "destinationPath",
        header: "Ścieżka docelowa",
        enableColumnFilter: false,
        size: 250,
        cell: (info) => (
          <code className="rounded bg-slate-100 px-2 py-1 text-sm dark:bg-slate-700">
            {info.getValue<string>()}
          </code>
        ),
      },
      {
        id: "isPermanent",
        accessorKey: "isPermanent",
        header: "Typ",
        enableColumnFilter: false,
        size: 140,
        cell: (info) =>
          info.getValue<boolean>() ? (
            <PaziaBadge color={"indigo" as ThemeColors}>301 Permanentne</PaziaBadge>
          ) : (
            <PaziaBadge color={"amber" as ThemeColors}>302 Tymczasowe</PaziaBadge>
          ),
      },
      {
        id: "description",
        accessorKey: "description",
        header: "Opis",
        enableColumnFilter: false,
        size: 200,
        cell: (info) => info.getValue<string | undefined>() ?? <span className="text-slate-400">—</span>,
      },
    ],
    [],
  );

  const showChainsPanel = !chainsLoading && chains.length > 0;

  const content = (
    <>
      {showChainsPanel && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-amber-900 dark:text-amber-200">
                {`Wykryto ${chains.length} łańcuchów przekierowań`}
              </div>
              <div className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                Łańcuchy spowalniają ładowanie i obniżają pozycjonowanie. Spłaszczenie sprawi, że pierwsze przekierowanie wskaże od razu na końcową stronę.
              </div>
            </div>
            <PaziaButton
              type="yellow"
              title="Spłaszcz wszystkie"
              btnType="button"
              processing={isFlattening}
              disabled={isFlattening}
              onClick={handleFlattenAll}
            />
          </div>
          <ul className="flex flex-col gap-2">
            {chains.map((chain, idx) => {
              const firstStepId = chain.steps[0]?.redirectId;
              const rowKey = `${firstStepId ?? "chain"}-${idx}`;
              const isRowFlattening = flatteningChainId === firstStepId;
              const pathNodes: string[] = [
                ...chain.steps.map((s) => s.sourcePath),
                ...(chain.isCircular ? [] : [chain.finalDestination]),
              ];
              return (
                <li
                  key={rowKey}
                  className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-white p-2 dark:border-amber-800 dark:bg-slate-800"
                >
                  <div className="flex flex-wrap items-center gap-1">
                    {pathNodes.map((p, i) => (
                      <React.Fragment key={i}>
                        <code className="rounded bg-slate-100 px-2 py-1 text-sm dark:bg-slate-700">
                          {p}
                        </code>
                        {i < pathNodes.length - 1 && (
                          <span className="text-slate-400">→</span>
                        )}
                      </React.Fragment>
                    ))}
                    {chain.isCircular && (
                      <PaziaBadge color={"red" as ThemeColors}>cykl</PaziaBadge>
                    )}
                  </div>
                  <PaziaButton
                    type="yellow2"
                    title="Spłaszcz"
                    btnType="button"
                    processing={isRowFlattening}
                    disabled={chain.isCircular || isFlattening || isRowFlattening}
                    onClick={() => handleFlattenOne(chain)}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
        <PaziaButton type="primary" title="Dodaj przekierowanie +" onClick={() => setIsAddOpen(true)} />
      </div>

      {isLoading ? (
        <PaziaLoader position="center" />
      ) : (
        <TanTableClient
          data={redirects}
          columns={columns}
          onEdit={(x) => setEditTarget(x)}
          onDelete={(x) => handleDelete(x)}
          hideBorder
        />
      )}

      <ShopRedirectModal
        isOpen={isAddOpen}
        onCloseRequest={() => setIsAddOpen(false)}
        onSubmit={handleAdd}
      />

      <ShopRedirectModal
        isOpen={editTarget != null}
        redirect={editTarget}
        onCloseRequest={() => setEditTarget(null)}
        onSubmit={handleEdit}
      />
    </>
  );

  if (embedded) return content;

  return (
    <PageMain>
      <PageRichHeader header="Przekierowania">
        <></>
      </PageRichHeader>
      <PaziaBox>{content}</PaziaBox>
    </PageMain>
  );
};
